import { Injectable } from '@angular/core';
import {
    type MapboxMap,
    PlannedRideWaypointContract,
    RiderColor,
} from '@polaris-customerweb/sharedcomponents.core';
import { ConsoleLogService } from '@polaris-customerweb/sharedcomponents.shared';
import {
    PlannedRide,
    PlannedRideViapoint,
    PlannedRideWaypoint,
} from '@polaris-customerweb/sharedcomponents.user-content';
import { compact, isEmpty } from 'lodash-es';
import type { PointLike, SourceSpecification } from 'mapbox-gl';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { GeoJsonFeatureCollection } from 'src/app/models/geojson-feature-collection';
import { GeocoderService } from 'src/app/services/geocoder.service';
import { PlannedRideService } from 'src/app/services/planned-ride.service';
import { UserContentColorHex } from '../types/user-content-color-hex';
import { MapAnimationService } from './map-animation.service';
import { MapContentMapService } from './map-content-map.service';
import { MapService } from './map.service';

@Injectable({ providedIn: 'root' })
export class PlannedRideMapService extends MapContentMapService {
    readonly className = 'PlannedRideMapService';
    adhocWaypoint?: PlannedRideWaypoint;
    minZoom = 8;
    sourceIdTempViapoint = 'plannedRidesTempViapoint';
    sourceIdTempWaypoint = 'plannedRidesTempWaypoint';
    sourceIdOfftrailUnselected = 'plannedridesofftrailunselected';
    sourceIdOfftrailSelected = 'plannedridesofftrailselected';

    loadingSpinnerLayer = 'offtraileditlabel';

    layerIdsTempWaypoint: string[];
    layerIdsTempViapoint: string[];
    layerIdsOfftrailUnselected: string[];
    layerIdsOfftrailSelected: string[];
    layerIdsLoadingSpinner: string[];

    // hack for location search results sharing planned ride temp waypoint layer
    private areEditLayersVisible = false;
    private spinnerColor: RiderColor = 'Blue';

    private emptySource: SourceSpecification = {
        data: new GeoJsonFeatureCollection([]),
        type: 'geojson',
    };

    constructor(
        consoleLogService: ConsoleLogService,
        private geocoderService: GeocoderService,
        private mapAnimationService: MapAnimationService,
        mapService: MapService,
        private plannedRideService: PlannedRideService
    ) {
        super(consoleLogService, mapService);
        super.setSourceId('plannedRides');
    }

    addSources(mapboxMap: MapboxMap): void {
        super.addSources(mapboxMap);

        this.layerIdsTempWaypoint = this.layerIds.filter((layerId) =>
            layerId.toLowerCase().includes('tempwaypoint')
        );
        this.layerIdsTempViapoint = this.layerIds.filter((layerId) =>
            layerId.toLowerCase().includes('tempviapoint')
        );

        const layerIds = this.mapService.getLayerIds(mapboxMap);
        this.layerIdsOfftrailSelected = layerIds.filter((layerId) =>
            layerId.toLowerCase().includes(this.sourceIdOfftrailSelected)
        );
        this.layerIdsOfftrailSelected.forEach((layerId) => {
            this.mapService.replaceLayer(mapboxMap, layerId, this.sourceId);
        });

        this.layerIdsSelected = [
            ...this.layerIdsSelected,
            ...this.layerIdsOfftrailSelected,
            ...this.layerIdsTempWaypoint,
        ];

        this.layerIdsOfftrailUnselected = layerIds.filter((layerId) =>
            layerId.toLowerCase().includes(this.sourceIdOfftrailUnselected)
        );

        this.layerIdsOfftrailUnselected.forEach((layerId) => {
            this.mapService.replaceLayer(mapboxMap, layerId, this.sourceId);
        });

        this.layerIdsUnselected = [
            ...this.layerIdsUnselected,
            ...this.layerIdsOfftrailUnselected,
        ];

        this.mapService.addSource(
            mapboxMap,
            this.sourceIdTempViapoint,
            this.emptySource
        );
        this.mapService.addSource(
            mapboxMap,
            this.sourceIdTempWaypoint,
            this.emptySource
        );

        this.layerIdsTempWaypoint.forEach((layerId) => {
            this.mapService.replaceLayer(
                mapboxMap,
                layerId,
                this.sourceIdTempWaypoint,
                undefined,
                false
            );
        });
        this.layerIdsTempViapoint.forEach((layerId) => {
            this.mapService.replaceLayer(
                mapboxMap,
                layerId,
                this.sourceIdTempViapoint,
                undefined,
                false
            );
        });

        // copy selected path and stops layers for editable planned ride
        this.layerIdsSelected
            .filter(
                (layerId) =>
                    layerId.toLowerCase().includes('path') ||
                    layerId.toLowerCase().includes('stops') ||
                    layerId.toLowerCase().includes('label')
            )
            .forEach((layerId) => {
                const editLayerId = layerId.replace('Selected', 'Edit');
                this.mapService.cloneLayer(
                    mapboxMap,
                    layerId,
                    editLayerId,
                    this.editSourceId
                );
                this.layerIdsEditing.push(editLayerId);
            });
        this.layerIds = [
            ...this.layerIdsEditing,
            ...this.layerIdsSelected,
            ...this.layerIdsUnselected,
        ];

        this.layerIdsLoadingSpinner = this.layerIdsEditing.filter((layerId) =>
            layerId.toLowerCase().includes(this.loadingSpinnerLayer)
        );

        this.upsertSpinnerImage(mapboxMap);
    }

    setSourceDataFromModel(mapboxMap: MapboxMap, plannedRides: PlannedRide[]) {
        const geoJson = plannedRides
            ? this.plannedRideService.plannedRidesToGeoJson(plannedRides)
            : undefined;
        super.setSourceData(mapboxMap, geoJson);
    }

    setEditSourceDataFromModel(
        mapboxMap: MapboxMap,
        plannedRide?: PlannedRide
    ) {
        if (plannedRide && plannedRide?.color !== this.spinnerColor) {
            this.spinnerColor = plannedRide.color || 'Blue';
            this.upsertSpinnerImage(mapboxMap);
        }
        const geoJson = plannedRide?.toGeoJson();
        super.setEditSourceData(mapboxMap, geoJson);
    }

    setTempViapoint(mapboxMap: MapboxMap, viapoint?: PlannedRideViapoint) {
        if (!viapoint) {
            this.mapService.setSourceData(
                mapboxMap,
                this.sourceIdTempViapoint,
                new GeoJsonFeatureCollection([])
            );
            this.consoleLogService.debug('clear temp viapoint map data');
            return;
        }

        this.mapService.setSourceData(
            mapboxMap,
            this.sourceIdTempViapoint,
            viapoint.toGeoJson()
        );
        this.consoleLogService.debug('set temp viapoint map data');
    }

    setAdhocWaypoint(mapboxMap: MapboxMap, waypoint?: PlannedRideWaypoint) {
        if (!waypoint) {
            this.mapService.setSourceData(
                mapboxMap,
                this.sourceIdTempWaypoint,
                new GeoJsonFeatureCollection([])
            );

            // hack for location search results sharing planned ride temp waypoint layer
            if (!this.areEditLayersVisible) {
                this.mapService.hideLayer(
                    mapboxMap,
                    'plannedRidesEditTempWaypoint'
                );
                this.mapService.setSelectedFilter(
                    mapboxMap,
                    'plannedRidesEditTempWaypoint',
                    undefined
                );
            }

            this.consoleLogService.debug('clear adhoc waypoint map data');
            return;
        }

        this.mapService.setSourceData(
            mapboxMap,
            this.sourceIdTempWaypoint,
            waypoint.toGeoJson()
        );

        // hack for location search results sharing planned ride temp waypoint layer
        this.mapService.showLayer(mapboxMap, 'plannedRidesEditTempWaypoint');
        this.mapService.setSelectedFilter(
            mapboxMap,
            'plannedRidesEditTempWaypoint',
            waypoint.id
        );

        this.consoleLogService.debug('set adhoc waypoint map data');
    }

    /*
        This method adds an AdhocWaypoint to the map.

        This is called when the user is in RidePlanning and clicks on the map. An Adhoc waypoint needs to be set.
        When this happens we do not have a name associated with the point, so we call the geocoder service
        and perform a lng/lat look up. When we do that, we take the name from the result.
    */
    getAdhocWaypointFromLngLat(
        mapboxMap: MapboxMap,
        lng: number,
        lat: number
    ): Observable<PlannedRideWaypoint> {
        const waypoint = PlannedRideWaypoint.fromLngLat(lng, lat);
        let name = $localize`Custom Stop`;
        let description = '';

        return this.geocoderService.getPlaceNamesByLngLat(lng, lat).pipe(
            map((adhocPoints) => {
                if (!isEmpty(adhocPoints)) {
                    const adhocPoint = adhocPoints.features[0];
                    const addressParts = [adhocPoint.address, adhocPoint.text];
                    name = compact(addressParts).join(' ');
                    const postCodeFeature = adhocPoints.features.filter(
                        (x) => x.place_type[0] === 'postcode'
                    )[0];

                    if (postCodeFeature && postCodeFeature !== adhocPoint) {
                        description = postCodeFeature.place_name;
                        // the place_name is the internationally correct order, so it's what we want for
                        // description, just sans country the country is always the last piece of the place_name,
                        // and is always prefixed by a comma.
                        // so we just split by comma, remove the last piece, and then join back together.
                        // it's gross but it lets mapbox do the hard work of ordering the address parts correctly.
                        const descriptionParts = description.split(',');
                        descriptionParts.splice(-1, 1); // remove country section
                        description = descriptionParts.join(',');
                        waypoint.description = description;
                    }
                }

                waypoint.name = name;
                waypoint.generatedName = name;
                this.adhocWaypoint = waypoint;

                return waypoint;
            }),
            catchError(() => {
                waypoint.name = name;
                this.adhocWaypoint = waypoint;
                this.mapService.setSourceData(
                    mapboxMap,
                    this.sourceIdTempWaypoint,
                    waypoint.toGeoJson()
                );
                this.consoleLogService.debug('set adhoc waypoint map data');

                return of(waypoint);
            })
        );
    }

    removeAdhocWaypoint(mapboxMap: MapboxMap) {
        this.adhocWaypoint = undefined;
        this.mapService.setSourceData(
            mapboxMap,
            this.sourceIdTempWaypoint,
            new GeoJsonFeatureCollection([])
        );
        this.consoleLogService.debug('clear adhoc waypoint map data');
    }

    getAdhocWaypointAtPoint(
        mapboxMap: MapboxMap,
        point: PointLike
    ): PlannedRideWaypoint | undefined {
        const features = this.mapService.getFeaturesAtPoint(mapboxMap, point, [
            this.sourceIdTempWaypoint,
        ]);
        let waypoint: PlannedRideWaypoint | undefined;

        if (!isEmpty(features)) {
            waypoint = new PlannedRideWaypoint(
                features[0] as PlannedRideWaypointContract
            );
        }

        return waypoint;
    }

    setEditLayersVisibility(mapboxMap: MapboxMap, visible: boolean) {
        // hack for location search results sharing planned ride temp waypoint layer
        this.areEditLayersVisible = visible;
        super.setEditLayersVisibility(mapboxMap, visible);
    }

    getPlannedRideAtPoint(
        mapboxMap: MapboxMap,
        point: PointLike
    ): PlannedRide | undefined {
        let features = this.mapService.getFeaturesAtPoint(
            mapboxMap,
            point,
            this.layerIds
        );
        let plannedRide: PlannedRide | undefined;
        features = features.filter(
            (feature) => feature.properties?.featureType === 'Waypoint'
        );

        if (!isEmpty(features)) {
            plannedRide = this.plannedRideService.getPlannedRideByIdFromCache(
                features[0].properties?.id as string
            );
        }

        return plannedRide;
    }

    private upsertSpinnerImage(mapboxMap: MapboxMap) {
        this.layerIdsLoadingSpinner.forEach((layerId) => {
            const spinnerName = `${layerId}Spinner`;
            const spinnerAnimation =
                this.mapAnimationService.createSpinnerAnimation(
                    mapboxMap,
                    UserContentColorHex[this.spinnerColor]
                );
            // updateImage does not update the render() method of the image, so we have to delete and re-add
            if (mapboxMap.hasImage(spinnerName)) {
                mapboxMap.removeImage(spinnerName);
            }
            mapboxMap.addImage(spinnerName, spinnerAnimation, {
                pixelRatio: spinnerAnimation.pixelRatio,
            });
            mapboxMap.setLayoutProperty(layerId, 'icon-image', spinnerName);
        });
    }
}
